import React, { ReactNode, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useOnClickOutside } from '@/hooks/';
import Close20Icon from '@/ui/Icons/Close20Icon';
import TrackingService from '@/services/tracking';
import { theme } from '@/styles';

interface TooltipProps {
  children: ReactNode;
  title?: string;
  text?: string;
  position?: 'left' | 'center';
  gtmName?: string;
}

const Tooltip = ({
  children,
  position = 'left',
  text,
  title,
  gtmName = '',
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setIsOpen(false));

  return (
    <TooltipContainer ref={ref} {...TrackingService.tagGTM(`${gtmName}`)}>
      <TooltipIcon
        onClick={() => setIsOpen(!isOpen)}
        {...TrackingService.tagGTM(`${gtmName}_button`)}
      >
        {children}
        {isOpen && (
          <TooltipBox position={position}>
            <TooltipHeader>
              <TooltipTitle>{title}</TooltipTitle>
              <CloseIconWrap
                onClick={() => setIsOpen(false)}
                {...TrackingService.tagGTM(`${gtmName}_close_button`)}
              />
            </TooltipHeader>
            <TooltipText>{text}</TooltipText>
          </TooltipBox>
        )}
      </TooltipIcon>
    </TooltipContainer>
  );
};

export default Tooltip;

const TooltipContainer = styled.div`
  position: relative;

  width: fit-content;
  height: fit-content;
`;

const TooltipBox = styled.div<{ position: 'left' | 'center' }>`
  position: absolute;
  z-index: 99;
  top: calc(100% + 4px);
  left: 0px;
  ${({ position }) =>
    position === 'center'
      ? css`
          left: 50%;
          transform: translateX(-50%);
        `
      : css`
          left: 0px;
        `};

  display: flex;
  flex-direction: column;
  gap: 4px;

  width: 232px;
  padding: 16px;
  background-color: ${theme.colors.neutral_colors.white};
  border: 1px solid ${theme.colors.neutral_colors.gray[500]};
  border-radius: 4px;

  color: ${theme.colors.accent_colors.primary.default};
`;

const TooltipIcon = styled.div`
  cursor: pointer;
`;

const CloseIconWrap = styled(Close20Icon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const TooltipHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 20px;
`;

const TooltipTitle = styled.div`
  display: block;
  flex: 1;
  ${theme.typography.headline7};
`;

const TooltipText = styled.div`
  display: block;
  ${theme.typography.body2};
`;
